import { looksLikeInvestor } from "./linkedin";
import type { Contact } from "./loop";

// ─────────────────────────────────────────────────────────────
// Labels — how a founder finds one person again.
//
// The circle stops being useful at around the fiftieth contact, when
// scrolling is no longer a way to find anyone. Labels and search are what
// turn "I met someone at SF Tech Week who does payments" from a memory
// problem into a query.
//
// Everything here proposes. Nothing here writes. `suggestedLabels` returns
// what Omina *would* say, and it lands on a contact only when she taps it —
// the same discipline as `metAt`, and for the same reason: three months
// later a stored guess is indistinguishable from something she confirmed.
// ─────────────────────────────────────────────────────────────

/** Longest label we store. Long enough for "met-sf-climate-tech-week". */
const MAX_LABEL = 40;

/**
 * Fold a label to its stored form: lowercase, spaces to hyphens, punctuation
 * dropped. Lowercasing is what lets a LIKE search match "Investor" typed in
 * the box against "investor" in the row.
 *
 * Returns null for anything that folds to nothing, so a stray space cannot
 * become an empty chip that is impossible to tap off again.
 */
export function normalizeLabel(raw: string): string | null {
  const folded = raw
    .normalize("NFD")
    .replace(COMBINING_MARKS, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, MAX_LABEL)
    // slice can leave a trailing hyphen behind; drop it again.
    .replace(/-+$/g, "");
  return folded || null;
}

/** Shared with normalizeName in linkedin.ts — see the note there about why
 *  this is built from a string instead of written as a literal range. */
const COMBINING_MARKS = new RegExp("[\\u0300-\\u036f]", "g");

/** Fold a list, drop blanks and duplicates, keep her order. */
export function normalizeLabels(raw: readonly string[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    const label = normalizeLabel(item);
    if (!label || seen.has(label)) continue;
    seen.add(label);
    out.push(label);
  }
  return out;
}

/** The label for "we met here" — `met-sf-tech-week`. This is the one that
 *  makes "who did I meet at X?" answerable by tapping instead of typing. */
export function eventLabel(eventTitle?: string | null): string | null {
  if (!eventTitle) return null;
  const slug = normalizeLabel(eventTitle);
  return slug ? `met-${slug}`.slice(0, MAX_LABEL).replace(/-+$/g, "") : null;
}

/**
 * What Omina would suggest for this contact, minus what is already applied.
 *
 * Every suggestion is derived from something already on the record — the room
 * she confirmed, or the role the person themselves publishes. Nothing is
 * inferred about someone from someone else, and nothing here is written.
 */
export function suggestedLabels(contact: Contact): string[] {
  const applied = new Set(contact.labels ?? []);
  const out: string[] = [];
  const add = (label: string | null) => {
    if (label && !applied.has(label) && !out.includes(label)) out.push(label);
  };

  // The room. Only from a confirmed or manually-entered metAt — a dismissed
  // one means she was asked and said no, so it is not a fact to label with.
  if (contact.metAt?.source !== "dismissed") add(eventLabel(contact.metAt?.eventTitle));

  // What they do, from their own words (role/company) or the research.
  if (looksLikeInvestor({ role: contact.role, company: contact.company })) add("investor");
  if (contact.enrichment?.investorProfile) add("investor");

  if (contact.company) add(normalizeLabel(contact.company));

  return out;
}

/**
 * Does this contact match what she typed?
 *
 * Matches across every field she might reach for, and — the point of the
 * whole feature — across the event she met them at, so typing "tech week"
 * answers "who did I meet at Tech Week?".
 *
 * Space-separated terms are ANDed: "sf investor" finds the investors from SF,
 * not everything matching either word. That is what people expect from a
 * search box, and ORing makes it useless the moment the circle is large.
 */
export function matchesQuery(contact: Contact, query: string): boolean {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (!terms.length) return true;

  const haystack = [
    contact.name,
    contact.company,
    contact.role,
    contact.context,
    contact.email,
    contact.metAt?.eventTitle,
    contact.metAt?.eventLocation,
    contact.enrichment?.currentRole,
    ...(contact.labels ?? []),
    // A hyphenated label should also match the words inside it, so typing
    // "tech week" finds `met-sf-tech-week`.
    ...(contact.labels ?? []).map((l) => l.replace(/-/g, " ")),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return terms.every((term) => haystack.includes(term));
}
